import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FLOWER_SECTIONS,
  PLACEHOLDERS,
  type CategoryId,
  type ProductPlaceholder,
  type QuantityPrice,
} from "@/data/catalog";
import {
  ADMIN_AUTH_EVENT,
  ADMIN_LOGOUT_EVENT,
  ADMIN_PIN,
  ADMIN_PIN_SESSION_KEY,
  SYNC_CLEARED_EVENT,
  SYNC_ERROR_EVENT,
} from "@/constants/admin";
import { publishSiteStateNow, writeAuthHeaders } from "@/lib/cloud-admin-sync";
import { uploadProductImage } from "@/lib/upload-product-image";
import { useAvailability } from "@/lib/availability";
import { useCatalog } from "@/lib/catalog-context";
import { config } from "@/lib/config";
import "./AdminPage.css";

type AdminMainTab = "menu" | "website";

type CatGroup = {
  key: string;
  label: string;
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function getAllGroups(): CatGroup[] {
  const groups: CatGroup[] = [];
  for (const section of FLOWER_SECTIONS) {
    groups.push({ key: `flower:${section.title}`, label: `Flower — ${section.title}` });
  }
  const labels: Record<string, string> = {
    preRolls: "Pre-rolls",
    edibles: "Edibles",
    specials: "Specials",
  };
  for (const key of Object.keys(PLACEHOLDERS)) {
    groups.push({ key, label: labels[key] ?? key });
  }
  return groups;
}

function ProductImageField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    setUploading(true);
    try {
      const { url } = await uploadProductImage(f);
      onChange(url);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="admin__photo-field">
      <div className="admin__photo-heading">
        <span className="admin__edit-label">Product photo</span>
        <span className="admin__edit-hint">Upload a file or paste a URL</span>
      </div>
      <div className="admin__photo-row">
        <div className="admin__photo-preview-wrap">
          {value.trim() ? (
            <img src={value} alt="" className="admin__photo-preview" />
          ) : (
            <div className="admin__photo-placeholder" aria-hidden>
              <span className="admin__photo-placeholder-icon">&#x1F33F;</span>
            </div>
          )}
        </div>
        <div className="admin__photo-buttons">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="admin__photo-file-input"
            onChange={onFileChange}
          />
          <button
            type="button"
            className="admin__photo-upload-btn"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
          {value.trim() ? (
            <button type="button" className="admin__photo-clear-btn" onClick={() => onChange("")}>
              Remove
            </button>
          ) : null}
        </div>
      </div>
      <label className="admin__edit-label">Image URL (optional)</label>
      <input
        className="admin__edit-input"
        placeholder="https://… or /media/… or /products/…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {err ? <p className="admin__photo-err">{err}</p> : null}
    </div>
  );
}

function ProductRow({
  product,
  onSave,
  onRemove,
  onPurge,
}: {
  product: ProductPlaceholder;
  onSave: (
    slug: string,
    changes: {
      name: string;
      tiers: QuantityPrice[];
      flavors?: string[];
      imageUrl: string | null;
    },
  ) => void;
  onRemove: (slug: string) => void;
  onPurge: (slug: string) => void;
}) {
  const { isAvailable, setAvailable, isFlavorAvailable, setFlavorAvailable } = useAvailability();
  const available = isAvailable(product.slug);

  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(product.name);
  const [tiers, setTiers] = useState<QuantityPrice[]>(product.tiers.map((t) => ({ ...t })));
  const [flavors, setFlavors] = useState(product.flavors?.join(", ") ?? "");
  const [photoUrl, setPhotoUrl] = useState(product.imageUrl ?? "");
  const [saved, setSaved] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setName(product.name);
    setTiers(product.tiers.map((t) => ({ ...t })));
    setFlavors(product.flavors?.join(", ") ?? "");
    setPhotoUrl(product.imageUrl ?? "");
  }, [product]);

  function doSave() {
    onSave(product.slug, {
      name: name.trim() || product.name,
      tiers: tiers.filter((t) => t.qty.trim() && t.price.trim()),
      flavors: flavors.trim()
        ? flavors
            .split(",")
            .map((f) => f.trim())
            .filter(Boolean)
        : undefined,
      imageUrl: photoUrl.trim() ? photoUrl.trim() : null,
    });
    setSaved(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSaved(false), 1500);
  }

  const dirty =
    name !== product.name ||
    JSON.stringify(tiers) !== JSON.stringify(product.tiers) ||
    flavors !== (product.flavors?.join(", ") ?? "") ||
    (photoUrl.trim() || null) !== (product.imageUrl?.trim() || null);

  const flavorList = useMemo(() => {
    if (product.flavors?.length) return product.flavors;
    if (!flavors.trim()) return [];
    return flavors
      .split(",")
      .map((f) => f.trim())
      .filter(Boolean);
  }, [product.flavors, flavors]);

  return (
    <li className="admin__item admin__item-card">
      <div className="admin__item-top">
        <div className="admin__item-thumb" aria-hidden={!product.imageUrl}>
          {product.imageUrl ? (
            <img src={product.imageUrl} alt="" className="admin__item-thumb-img" />
          ) : (
            <span className="admin__item-thumb-placeholder">&#x1F33F;</span>
          )}
        </div>
        <div className="admin__item-info">
          <span className="admin__item-name">{product.name}</span>
          <span className="admin__item-tiers">
            {product.tiers.map((t) => `${t.qty}: ${t.price}`).join(" · ")}
          </span>
        </div>
        <div className="admin__item-actions">
          <button
            type="button"
            className={`admin__toggle ${available ? "admin__toggle--on" : "admin__toggle--off"}`}
            onClick={() => setAvailable(product.slug, !available)}
          >
            <span className="admin__toggle-knob" />
            <span className="admin__toggle-label">{available ? "In stock" : "Out of stock"}</span>
          </button>
          <button type="button" className="admin__edit-btn" onClick={() => setExpanded((e) => !e)}>
            {expanded ? "Close" : "Edit"}
          </button>
          <button
            type="button"
            className="admin__delete-btn"
            onClick={() => onRemove(product.slug)}
          >
            Hide from menu
          </button>
          <button
            type="button"
            className="admin__purge-btn"
            onClick={() => onPurge(product.slug)}
          >
            Delete forever
          </button>
        </div>
      </div>

      {flavorList.length > 0 && (
        <div className="admin__flavor-stock" aria-label="Flavor availability">
          <p className="admin__flavor-heading">By flavor</p>
          {flavorList.map((fl) => {
            const flOk = isFlavorAvailable(product.slug, fl);
            return (
              <div key={fl} className="admin__flavor-row">
                <span className="admin__flavor-name">{fl}</span>
                <button
                  type="button"
                  className={`admin__toggle admin__toggle--sm ${flOk ? "admin__toggle--on" : "admin__toggle--off"}`}
                  onClick={() => setFlavorAvailable(product.slug, fl, !flOk)}
                >
                  <span className="admin__toggle-knob" />
                  <span className="admin__toggle-label">
                    {flOk ? "In stock" : "Out of stock"}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {expanded && (
        <div className="admin__edit-form">
          <div className="admin__edit-row">
            <label className="admin__edit-label">Name</label>
            <input
              className="admin__edit-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <ProductImageField value={photoUrl} onChange={setPhotoUrl} />

          <div className="admin__edit-row">
            <label className="admin__edit-label">Options</label>
            {tiers.map((tier, i) => (
              <div key={i} className="admin__tier-row">
                <input
                  className="admin__edit-input admin__edit-input--sm"
                  placeholder="Qty (e.g. 1/4 oz)"
                  value={tier.qty}
                  onChange={(e) => {
                    const next = [...tiers];
                    next[i] = { ...next[i], qty: e.target.value };
                    setTiers(next);
                  }}
                />
                <input
                  className="admin__edit-input admin__edit-input--sm"
                  placeholder="Price (e.g. $60)"
                  value={tier.price}
                  onChange={(e) => {
                    const next = [...tiers];
                    next[i] = { ...next[i], price: e.target.value };
                    setTiers(next);
                  }}
                />
                <button
                  type="button"
                  className="admin__tier-remove"
                  onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              className="admin__tier-add"
              onClick={() => setTiers([...tiers, { qty: "", price: "" }])}
            >
              + Add option
            </button>
          </div>

          <div className="admin__edit-row">
            <label className="admin__edit-label">
              Flavors <span className="admin__edit-hint">(comma-separated, or leave blank)</span>
            </label>
            <input
              className="admin__edit-input"
              placeholder="e.g. Lime, Peach, Watermelon"
              value={flavors}
              onChange={(e) => setFlavors(e.target.value)}
            />
          </div>

          <div className="admin__edit-actions">
            <button
              type="button"
              className={`admin__save-btn ${saved ? "admin__save-btn--saved" : ""}`}
              onClick={doSave}
              disabled={!dirty && !saved}
            >
              {saved ? "Saved!" : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export function AdminPage() {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const {
    flowerSections,
    placeholders,
    updateProduct,
    removeProduct,
    purgeProduct,
    restoreProduct,
    getHiddenProductsForGroup,
    addProduct,
    resetCatalog,
    overrides: catalogOverrides,
  } = useCatalog();
  const {
    resetAvailability,
    clearAvailabilityForProduct,
    overrides: availabilityOverrides,
  } = useAvailability();

  const [publishStatus, setPublishStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [publishMessage, setPublishMessage] = useState("");

  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newTiers, setNewTiers] = useState<QuantityPrice[]>([{ qty: "", price: "" }]);
  const [newFlavors, setNewFlavors] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");

  const [deployStatus, setDeployStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [deployMessage, setDeployMessage] = useState("");
  const [syncError, setSyncError] = useState<string | null>(null);
  const [mainTab, setMainTab] = useState<AdminMainTab>("menu");

  /** Writes: JSON `pin` vs server `ADMIN_DEPLOY_PIN` — no browser platform tokens for staff. */
  const liveSiteKvReady = Boolean(ADMIN_PIN) && authed;
  const onlineSaveHealthy = liveSiteKvReady && !syncError;

  useEffect(() => {
    if (!ADMIN_PIN) return;
    if (sessionStorage.getItem(ADMIN_PIN_SESSION_KEY) === ADMIN_PIN) {
      setAuthed(true);
      window.dispatchEvent(new Event(ADMIN_AUTH_EVENT));
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem("jmanatx_site_state_write_token");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    function onSyncErr(e: Event) {
      const d = (e as CustomEvent<{ message?: string }>).detail;
      if (d?.message) setSyncError(d.message);
    }
    function onSyncClear() {
      setSyncError(null);
    }
    window.addEventListener(SYNC_ERROR_EVENT, onSyncErr);
    window.addEventListener(SYNC_CLEARED_EVENT, onSyncClear);
    return () => {
      window.removeEventListener(SYNC_ERROR_EVENT, onSyncErr);
      window.removeEventListener(SYNC_CLEARED_EVENT, onSyncClear);
    };
  }, []);

  function handleLogin(e: FormEvent) {
    e.preventDefault();
    if (!ADMIN_PIN) {
      setError(
        "This deployment has no store PIN. The person who publishes the website must add one in hosting settings and publish again.",
      );
      return;
    }
    if (pin.trim() === ADMIN_PIN) {
      sessionStorage.setItem(ADMIN_PIN_SESSION_KEY, pin.trim());
      setAuthed(true);
      window.dispatchEvent(new Event(ADMIN_AUTH_EVENT));
      setError("");
    } else {
      setError("Wrong PIN.");
    }
  }

  function handleSave(
    slug: string,
    changes: {
      name: string;
      tiers: QuantityPrice[];
      flavors?: string[];
      imageUrl: string | null;
    },
  ) {
    updateProduct(slug, changes);
  }

  function handleRemove(slug: string) {
    removeProduct(slug);
  }

  function handlePurge(slug: string) {
    if (
      !window.confirm(
        "Permanently delete this product? It will disappear from the menu and cannot be restored (unless you add a new product with the same name later).",
      )
    ) {
      return;
    }
    purgeProduct(slug);
    clearAvailabilityForProduct(slug);
  }

  function startAdd(categoryKey: string) {
    setAddingTo(categoryKey);
    setNewName("");
    setNewTiers([{ qty: "", price: "" }]);
    setNewFlavors("");
    setNewImageUrl("");
  }

  function saveAdd() {
    if (!addingTo || !newName.trim()) return;
    const validTiers = newTiers.filter((t) => t.qty.trim() && t.price.trim());
    if (validTiers.length === 0) return;
    addProduct(addingTo, {
      name: newName.trim(),
      slug: slugify(newName),
      tiers: validTiers,
      flavors: newFlavors.trim()
        ? newFlavors
            .split(",")
            .map((f) => f.trim())
            .filter(Boolean)
        : undefined,
      imageUrl: newImageUrl.trim() ? newImageUrl.trim() : undefined,
    });
    setAddingTo(null);
  }

  const groups = getAllGroups();

  async function handlePublishNow() {
    setPublishStatus("loading");
    setPublishMessage("");
    const result = await publishSiteStateNow(catalogOverrides, availabilityOverrides);
    if (result.ok) {
      setPublishStatus("ok");
      setPublishMessage("Live site updated. Everyone will see this version.");
    } else {
      setPublishStatus("err");
      setPublishMessage(result.error ?? "Publish failed.");
    }
  }

  async function triggerDeployment() {
    setDeployStatus("loading");
    setDeployMessage("");
    try {
      const r = await fetch("/api/deploy", {
        method: "POST",
        headers: writeAuthHeaders(),
        body: JSON.stringify({ pin: ADMIN_PIN }),
      });
      const data = (await r.json().catch(() => ({}))) as { error?: string; message?: string };
      if (!r.ok) {
        throw new Error(data.error || `Request failed (${r.status})`);
      }
      setDeployStatus("ok");
      setDeployMessage(data.message ?? "Deployment triggered.");
    } catch (e) {
      setDeployStatus("err");
      setDeployMessage(
        e instanceof Error
          ? e.message
          : "Failed. Git deploy from the dashboard only works on the hosted site when your developer configured it.",
      );
    }
  }

  function getProducts(groupKey: string): ProductPlaceholder[] {
    if (groupKey.startsWith("flower:")) {
      const title = groupKey.replace("flower:", "");
      const section = flowerSections.find((s) => s.title === title);
      return section?.strains ?? [];
    }
    return placeholders[groupKey as Exclude<CategoryId, "flower">] ?? [];
  }

  if (!authed) {
    return (
      <div className="admin admin--login">
        <div className="admin__card admin__card--login">
          <p className="admin__card-kicker">Store admin</p>
          <h1 className="admin__title">Welcome back</h1>
          <p className="admin__desc">
            Enter your PIN to manage your menu, stock, and what customers see on your website.
          </p>
          {!ADMIN_PIN ? (
            <div className="admin__login-setup" role="alert">
              <p className="admin__error admin__error--block">
                This build does not include a store PIN yet. Only the person who publishes the website can fix that
                — store managers are <strong>not</strong> asked to log into hosting or paste platform keys.
              </p>
              <details className="admin__help admin__help--dev admin__help--login">
                <summary className="admin__help-summary">Site owner / developer</summary>
                <p className="admin__help-lead">
                  Set <code className="admin__code">VITE_ADMIN_PIN</code> at <strong>build</strong> time and{" "}
                  <code className="admin__code">ADMIN_DEPLOY_PIN</code> (same string) for{" "}
                  <strong>Functions / runtime</strong> on your host, then redeploy.
                </p>
              </details>
            </div>
          ) : (
            <form className="admin__form" onSubmit={handleLogin}>
              <label className="admin__label-visually" htmlFor="admin-pin">
                PIN
              </label>
              <input
                id="admin-pin"
                type="password"
                className="admin__input"
                placeholder="Enter PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                maxLength={128}
                autoFocus
                autoComplete="current-password"
              />
              <button type="submit" className="admin__btn">
                Continue
              </button>
            </form>
          )}
          {error && <p className="admin__error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin admin--dashboard">
      <header className="admin__topbar">
        <div className="admin__topbar-inner">
          <div className="admin__topbar-brand">
            <h1 className="admin__topbar-title">Store dashboard</h1>
            <p className="admin__topbar-tagline">
              Update your menu and online stock in one place.
            </p>
            <button
              type="button"
              className={`admin__status-pill ${onlineSaveHealthy ? "admin__status-pill--ok" : "admin__status-pill--warn"}`}
              onClick={() => setMainTab("website")}
              title={
                onlineSaveHealthy
                  ? "Menu and online stock — your store PIN is all you need here"
                  : "Could not save online — see Live website for details"
              }
            >
              {onlineSaveHealthy ? "Signed in" : "Online save issue — tap for details"}
            </button>
          </div>
          <div className="admin__topbar-actions">
            <a
              className="admin__ghost-link"
              href={config.publicSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View store
            </a>
            <button
              type="button"
              className="admin__reset-btn"
              onClick={() => {
                if (
                  !window.confirm(
                    "Reset the entire menu and stock to defaults? This cannot be undone.",
                  )
                ) {
                  return;
                }
                resetCatalog();
                resetAvailability();
              }}
            >
              Reset all
            </button>
            <button
              type="button"
              className="admin__logout"
              onClick={() => {
                window.dispatchEvent(new Event(ADMIN_LOGOUT_EVENT));
                setAuthed(false);
                sessionStorage.removeItem(ADMIN_PIN_SESSION_KEY);
              }}
            >
              Log out
            </button>
          </div>
        </div>

        <div
          className="admin__tabs"
          role="tablist"
          aria-label="Dashboard sections"
        >
          <button
            type="button"
            role="tab"
            id="tab-menu"
            aria-selected={mainTab === "menu"}
            aria-controls="panel-menu"
            className={`admin__tab ${mainTab === "menu" ? "admin__tab--active" : ""}`}
            onClick={() => setMainTab("menu")}
          >
            Menu &amp; products
          </button>
          <button
            type="button"
            role="tab"
            id="tab-website"
            aria-selected={mainTab === "website"}
            aria-controls="panel-website"
            className={`admin__tab ${mainTab === "website" ? "admin__tab--active" : ""}`}
            onClick={() => setMainTab("website")}
          >
            Live website
          </button>
        </div>
      </header>

      <main className="admin__main">
        {mainTab === "menu" && (
          <div
            className="admin__sheet"
            id="panel-menu"
            role="tabpanel"
            aria-labelledby="tab-menu"
          >
            <section className="admin__intro" aria-labelledby="intro-heading">
              <h2 id="intro-heading" className="admin__intro-title">
                Your menu
              </h2>
              <p className="admin__intro-text">
                Use <strong>Stock</strong> to mark a whole product in or out of stock. If a product has
                flavors, adjust each flavor below. <strong>Hide from menu</strong> is reversible in
                “Hidden from menu”. <strong>Delete forever</strong> removes the product for good.
              </p>
            </section>

            {groups.map((group) => {
              const products = getProducts(group.key);
              return (
                <div key={group.key} className="admin__group">
                  <div className="admin__group-header">
                    <h2 className="admin__group-title">{group.label}</h2>
                    <button
                      type="button"
                      className="admin__add-btn"
                      onClick={() => startAdd(group.key)}
                    >
                      Add product
                    </button>
                  </div>

                  <ul className="admin__list admin__list--cards">
                    {products.map((product) => (
                      <ProductRow
                        key={product.slug}
                        product={product}
                        onSave={handleSave}
                        onRemove={handleRemove}
                        onPurge={handlePurge}
                      />
                    ))}
                    {products.length === 0 && (
                      <li className="admin__item admin__item--empty">No products in this category.</li>
                    )}
                  </ul>

                  {(() => {
                    const hidden = getHiddenProductsForGroup(group.key);
                    if (hidden.length === 0) return null;
                    return (
                      <div className="admin__hidden-block">
                        <h3 className="admin__hidden-title">Hidden from menu</h3>
                        <p className="admin__hidden-desc">
                          Bring a product back here with the same prices and options as before.
                        </p>
                        <ul className="admin__list admin__list--cards">
                          {hidden.map((p) => (
                            <li
                              key={p.slug}
                              className="admin__item admin__item-card admin__item--hidden"
                            >
                              <div className="admin__item-top">
                                <div className="admin__item-info">
                                  <span className="admin__item-name">{p.name}</span>
                                  <span className="admin__item-tiers">
                                    {p.tiers.map((t) => `${t.qty}: ${t.price}`).join(" · ")}
                                  </span>
                                </div>
                                <div className="admin__hidden-actions">
                                  <button
                                    type="button"
                                    className="admin__restore-btn"
                                    onClick={() => restoreProduct(p.slug)}
                                  >
                                    Restore to menu
                                  </button>
                                  <button
                                    type="button"
                                    className="admin__purge-btn"
                                    onClick={() => handlePurge(p.slug)}
                                  >
                                    Delete forever
                                  </button>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}

                  {addingTo === group.key && (
                    <div className="admin__add-form">
                      <h3 className="admin__add-title">New product — {group.label}</h3>
                      <div className="admin__edit-row">
                        <label className="admin__edit-label">Name</label>
                        <input
                          className="admin__edit-input"
                          placeholder="Product name"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <ProductImageField value={newImageUrl} onChange={setNewImageUrl} />
                      <div className="admin__edit-row">
                        <label className="admin__edit-label">Options</label>
                        {newTiers.map((tier, i) => (
                          <div key={i} className="admin__tier-row">
                            <input
                              className="admin__edit-input admin__edit-input--sm"
                              placeholder="Qty"
                              value={tier.qty}
                              onChange={(e) => {
                                const next = [...newTiers];
                                next[i] = { ...next[i], qty: e.target.value };
                                setNewTiers(next);
                              }}
                            />
                            <input
                              className="admin__edit-input admin__edit-input--sm"
                              placeholder="Price"
                              value={tier.price}
                              onChange={(e) => {
                                const next = [...newTiers];
                                next[i] = { ...next[i], price: e.target.value };
                                setNewTiers(next);
                              }}
                            />
                            {newTiers.length > 1 && (
                              <button
                                type="button"
                                className="admin__tier-remove"
                                onClick={() => setNewTiers(newTiers.filter((_, j) => j !== i))}
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="admin__tier-add"
                          onClick={() => setNewTiers([...newTiers, { qty: "", price: "" }])}
                        >
                          + Add option
                        </button>
                      </div>
                      <div className="admin__edit-row">
                        <label className="admin__edit-label">
                          Flavors{" "}
                          <span className="admin__edit-hint">(comma-separated, or leave blank)</span>
                        </label>
                        <input
                          className="admin__edit-input"
                          placeholder="e.g. Lime, Peach, Watermelon"
                          value={newFlavors}
                          onChange={(e) => setNewFlavors(e.target.value)}
                        />
                      </div>
                      <div className="admin__edit-actions">
                        <button
                          type="button"
                          className="admin__save-btn"
                          onClick={saveAdd}
                          disabled={
                            !newName.trim() || !newTiers.some((t) => t.qty.trim() && t.price.trim())
                          }
                        >
                          Add product
                        </button>
                        <button
                          type="button"
                          className="admin__cancel-btn"
                          onClick={() => setAddingTo(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {mainTab === "website" && (
          <div
            className="admin__sheet"
            id="panel-website"
            role="tabpanel"
            aria-labelledby="tab-website"
          >
            <section className="admin__intro admin__intro--compact" aria-labelledby="web-intro-heading">
              <h2 id="web-intro-heading" className="admin__intro-title">
                Live website
              </h2>
              <p className="admin__intro-text">
                <strong>Store staff</strong> only use the same PIN as on the login screen. Nobody here is asked to
                open a hosting account, create platform tokens, or share access with your business software vendor.
              </p>
              <p className="admin__intro-text admin__intro-text--secondary">
                Publish or push updates below. If something fails, forward the error message to whoever deploys the
                website — not to a store-suite admin who does not control hosting.
              </p>
            </section>

            <div className="admin__deploy admin__live-site">
              <div
                className={`admin__sync-strip ${liveSiteKvReady ? "admin__sync-strip--ok" : "admin__sync-strip--warn"}`}
                role="status"
              >
                {liveSiteKvReady
                  ? "Your PIN authorizes updates to the live menu and online stock."
                  : "Your session could not be confirmed. Sign out and sign in with your store PIN again."}
              </div>

              {syncError && (
                <div className="admin__sync-alert" role="alert">
                  <p className="admin__sync-alert-title">Could not save online</p>
                  <p className="admin__sync-alert-body">{syncError}</p>
                  <button
                    type="button"
                    className="admin__sync-alert-dismiss"
                    onClick={() => setSyncError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              )}

              <div className="admin__push-now-block">
                <p className="admin__push-now-desc">
                  Save menu changes on <strong>Menu &amp; products</strong> first. Use this button if you need
                  to force everything to the live site right away.
                </p>
                <button
                  type="button"
                  className="admin__publish-now"
                  onClick={() => void handlePublishNow()}
                  disabled={publishStatus === "loading"}
                >
                  {publishStatus === "loading" ? "Sending…" : "Send updates now"}
                </button>
              </div>

              {publishStatus === "ok" && publishMessage && (
                <p className="admin__deploy-ok">{publishMessage}</p>
              )}
              {publishStatus === "err" && publishMessage && (
                <p className="admin__deploy-err">{publishMessage}</p>
              )}

              <details className="admin__help admin__help--dev">
                <summary className="admin__help-summary">Developer / site owner (hosting — not store staff)</summary>
                <p className="admin__help-lead">
                  <strong>Build + Workers (e.g. Cloudflare Pages):</strong>{" "}
                  <code className="admin__code">VITE_ADMIN_PIN</code> = PIN staff type;{" "}
                  <code className="admin__code">ADMIN_DEPLOY_PIN</code> = same string for Functions runtime. Redeploy
                  after changing <code className="admin__code">VITE_*</code>. Optional zero-trust in front of{" "}
                  <code className="admin__code">/admin</code> is a separate product (e.g. Cloudflare Access).
                </p>
                <p className="admin__help-lead">
                  <strong>Optional Bearer (same value):</strong> <code className="admin__code">SITE_STATE_WRITE_TOKEN</code>{" "}
                  (Functions) + <code className="admin__code">VITE_SITE_STATE_WRITE_TOKEN</code> (build) — the admin app
                  adds <code className="admin__code">Authorization</code> after PIN login; no typing in the browser.
                  <code className="admin__code">GUEST_ADMIN_TOKEN</code> is for scripts only.
                </p>
                <p className="admin__help-lead">
                  <strong>Deploy CLI:</strong> API tokens stay on the machine that runs Wrangler — never in the store
                  dashboard.
                </p>
              </details>

            <details className="admin__deploy admin__advanced">
              <summary className="admin__advanced-summary">Advanced: rebuild from Git (optional)</summary>
              <p className="admin__deploy-desc">
                Only if your developer set up a deploy hook. Starts a new build from your connected branch. Needs{" "}
                <code className="admin__code">DEPLOY_HOOK_URL</code> configured in hosting for this project.
              </p>
              <button
                type="button"
                className="admin__deploy-btn"
                onClick={() => void triggerDeployment()}
                disabled={deployStatus === "loading"}
              >
                {deployStatus === "loading" ? "Starting…" : "Run Git deployment"}
              </button>
              {deployStatus === "ok" && <p className="admin__deploy-ok">{deployMessage}</p>}
              {deployStatus === "err" && <p className="admin__deploy-err">{deployMessage}</p>}
            </details>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
